import { useQuery } from '@apollo/client';
import { Box, Grid, GridItem, HStack, Text, VStack } from '@chakra-ui/react';
import { SnippetBox } from '@gamma/ui/display';
import { RouterLink } from '@gamma/ui/navigation';
import { LoadingSpinner } from '@gamma/ui/progress';
import { FC, useContext, useMemo } from 'react';
import dedent from 'ts-dedent';
import { TruncatedText } from '@investigator/shared/components';
import { ROUTES } from '@investigator/shared/constants';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import formatUnixTimeStampAsDisplayString from '@investigator/shared/utils/formatUnixTimeStampAsDisplayString';
import { DetailsGridRow } from '../../../../../components';
import { GET_YARA_ALERT_METADATA } from '../../../../../queries';
import { LayoutProps } from '../types';
import { stringifyAlertType } from '@investigator/shared/utils';
import { OrgTenantsContext } from '@investigator/shared/providers/OrgTenantsProvider/OrgTenantsContext';

const {
  detection: detectionText,
  detectionSummary: {
    yaraMetadataError: yaraMetadataErrorLocale,
    yaraMetadataNotFound,
  },
} = i18n.pages.detections;

const { systemSettingsAlertCatalog } = ROUTES;

const Yara: FC<LayoutProps> = ({ detectionInfo }) => {
  const { start, end } = useDateRangeQueryString();
  const { orgTenantsQueryParam } = useContext(OrgTenantsContext);

  const {
    alertCategory,
    numberOfAlerts,
    type,
    firstTimeSeen,
    lastTimeSeen,
    yaraRuleMetaData,
  } = detectionText;

  const {
    alert_info,
    total_alert_count,
    earliest_start_timestamp,
    latest_start_timestamp,
  } = detectionInfo;

  const tenantId = detectionInfo?.tenant_info?.tenant_id ?? '';
  const contentId = alert_info?.content_id || '';

  const skipYaraMetadata = !tenantId.length || !contentId.length;

  const {
    data: yaraMetadataData,
    loading: yaraMetadataLoading,
    error: yaraMetadataError,
  } = useQuery(GET_YARA_ALERT_METADATA, {
    skip: skipYaraMetadata,
    variables: {
      tenant: tenantId,
      content_id: contentId,
    },
  });

  const yaraMatchMeta = useMemo(() => {
    if (!yaraMetadataData?.getAlertMetadata?.metadata?.[0]?.match_meta) {
      return '';
    }
    return JSON.stringify(
      yaraMetadataData.getAlertMetadata.metadata[0].match_meta,
      null,
      2,
    );
  }, [yaraMetadataData]);

  return (
    <VStack w="full" align="stretch" spacing={4}>
      <HStack align="start">
        <Box flex="1">
          <VStack alignItems="start">
            <HStack w="100%" alignItems="start">
              {alert_info?.alert_name && (
                <Grid templateColumns="1fr" width="100%">
                  <GridItem>
                    <Text color="text.secondary">{alertCategory}</Text>
                  </GridItem>
                  <GridItem
                    data-testid="detection-summary-alert-name-value"
                    overflow="hidden"
                    minW={0}
                  >
                    <TruncatedText>
                      <RouterLink
                        to={`${systemSettingsAlertCatalog(
                          `/${alert_info?.content_id}?start=${start}&end=${end}&tenant=${orgTenantsQueryParam}`,
                        )}`}
                        data-testid="pivot-detection-alert-catalog-link"
                        color="text.link"
                      >
                        {alert_info?.alert_name}
                      </RouterLink>
                    </TruncatedText>
                  </GridItem>
                </Grid>
              )}
              {alert_info?.alert_type && (
                <DetailsGridRow
                  title={type}
                  isDetailsPage
                  data-testid="detection-summary-type"
                >
                  <Text>{stringifyAlertType(alert_info?.alert_type)}</Text>
                </DetailsGridRow>
              )}
              {total_alert_count !== null && (
                <DetailsGridRow
                  isDetailsPage
                  title={numberOfAlerts}
                  data-testid="detection-summary-alert-count"
                >
                  <Text>{total_alert_count}</Text>
                </DetailsGridRow>
              )}
              {earliest_start_timestamp && (
                <DetailsGridRow
                  isDetailsPage
                  title={firstTimeSeen}
                  data-testid="detection-summary-first-timestamp"
                >
                  <Text>
                    {formatUnixTimeStampAsDisplayString(
                      earliest_start_timestamp,
                    )}
                  </Text>
                </DetailsGridRow>
              )}
              {latest_start_timestamp && (
                <DetailsGridRow
                  isDetailsPage
                  title={lastTimeSeen}
                  data-testid="detection-summary-last-timestamp"
                >
                  <Text>
                    {formatUnixTimeStampAsDisplayString(latest_start_timestamp)}
                  </Text>
                </DetailsGridRow>
              )}
              <DetailsGridRow
                isDetailsPage
                title={''}
                data-testid="detection-summary-blank-col-to-match-design"
              >
                {null}
              </DetailsGridRow>
            </HStack>
          </VStack>
        </Box>
      </HStack>
      <HStack align="start">
        <Box flex="1">
          <VStack alignItems="start">
            <DetailsGridRow
              isDetailsPage
              title={yaraRuleMetaData}
              data-testid="detection-yara-snippet"
            >
              {yaraMetadataLoading ? (
                <LoadingSpinner />
              ) : yaraMetadataError ? (
                <Text color="red.300">
                  {i18n.formatString(
                    yaraMetadataErrorLocale,
                    yaraMetadataError.message,
                  )}
                </Text>
              ) : yaraMatchMeta ? (
                <SnippetBox
                  snippet={dedent`${yaraMatchMeta}`}
                  isCopyable={true}
                />
              ) : (
                <Text>{yaraMetadataNotFound}</Text>
              )}
            </DetailsGridRow>
          </VStack>
        </Box>
      </HStack>
    </VStack>
  );
};

export default Yara;
